/**
 * ListMovimientosBancarios.js — Consulta de movimientos bancarios (solo lectura).
 * Muestra todos los BancoMovimiento del condominio (originados por recibos,
 * traslados, conciliaciones, etc.) con filtros y exportación a Excel.
 * El superadministrador además puede corregirlos (crear, editar, eliminar,
 * detectar duplicados y ver la bitácora): ver components/MovimientoAdmin.js.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { TableToolbar, FilterField, FilterInput, FilterSelect } from "../../shared/TableToolbar";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import {
  EliminarMovimientoModal, EditarMovimientoModal, CrearMovimientoModal,
  DuplicadosModal, BitacoraModal, IconBtn,
} from "../components/MovimientoAdmin";

function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function ListMovimientosBancarios() {
  const { t } = useTranslation();
  const [items,       setItems]       = useState([]);
  const [count,       setCount]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [search,      setSearch]      = useState("");
  const [fechaDesde,  setFechaDesde]  = useState("");
  const [fechaHasta,  setFechaHasta]  = useState("");
  const [cuentaId,    setCuentaId]    = useState("");
  const [cuentas,     setCuentas]     = useState([]);
  const [totales,     setTotales]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [exporting,   setExporting]   = useState(false);

  // Correcciones manuales: solo el superadministrador.
  const isSuperUser = localStorage.getItem("is_superuser") === "true";
  const [modal,       setModal]       = useState(null);  // {tipo, id?}
  const [verDuplicados, setVerDuplicados] = useState(false);
  const [versionDup,  setVersionDup]  = useState(0);
  const [aviso,       setAviso]       = useState("");
  const COLS = isSuperUser ? 8 : 7;

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const activeFilterCount = [fechaDesde, fechaHasta, cuentaId].filter(Boolean).length;

  const cuentaNombre = (id) => {
    const c = cuentas.find((c) => String(c.id) === String(id));
    return c ? `${c.banco} - ${c.numero_cuenta}` : "-";
  };
  const cuentaSimbolo = (id) => {
    const c = cuentas.find((c) => String(c.id) === String(id));
    return c?.moneda === "USD" ? "$" : c?.moneda === "EUR" ? "€" : "₡";
  };

  function buildParams(pageSize, pageNum) {
    const qs = new URLSearchParams({ page: pageNum, page_size: pageSize });
    if (search.trim()) qs.set("search", search.trim());
    if (fechaDesde)     qs.set("fecha_desde", fechaDesde);
    if (fechaHasta)     qs.set("fecha_hasta", fechaHasta);
    if (cuentaId)       qs.set("cuenta_bancaria", cuentaId);
    return qs;
  }

  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/finance/bancoMovimiento/?${buildParams(PAGE_SIZE, page)}`)
      .then((r) => r.json())
      .then((d) => { setItems(d.results ?? []); setCount(d.count ?? 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, fechaDesde, fechaHasta, cuentaId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Totales de TODO lo que pasa el filtro, no solo de la página visible: el
  // backend los suma aparte porque la página trae 20 filas de las que puede
  // haber cientos.
  const fetchTotales = useCallback(() => {
    const qs = buildParams(PAGE_SIZE, 1);
    qs.delete("page");
    qs.delete("page_size");
    apiFetch(`/api/finance/bancoMovimiento/totales/?${qs}`)
      .then((r) => r.json())
      .then((d) => setTotales(d.totales ?? []))
      .catch(() => setTotales([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fechaDesde, fechaHasta, cuentaId]);

  useEffect(() => { fetchTotales(); }, [fetchTotales]);

  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/?all=1")
      .then((r) => r.json())
      .then((d) => setCuentas(Array.isArray(d) ? d : d.results ?? []))
      .catch(() => {});
  }, []);

  function alTerminar(msg) {
    setModal(null);
    setAviso(msg);
    setTimeout(() => setAviso(""), 6000);
    setVersionDup(v => v + 1);
    fetchData();
    fetchTotales();
  }

  async function exportarExcel() {
    setExporting(true);
    try {
      const res = await apiFetch(`/api/finance/bancoMovimiento/?${buildParams(10000, 1)}`);
      const d = await res.json();
      const filas = (d.results ?? []).map((m) => ({
        [t("movimientos.colFecha")]:       fmtDate(m.fecha),
        [t("movimientos.colCuenta")]:      cuentaNombre(m.cuenta_bancaria),
        [t("movimientos.colDocumento")]:   m.documento || "",
        [t("movimientos.colDescripcion")]: m.descripcion || "",
        [t("movimientos.colDebito")]:      Number(m.debito) || 0,
        [t("movimientos.colCredito")]:     Number(m.credito) || 0,
        [t("movimientos.colEstado")]: m.conciliacion
          ? t("movimientos.estadoConciliado")
          : t("movimientos.estadoPendiente"),
      }));
      const ws = XLSX.utils.json_to_sheet(filas);
      ws["!cols"] = [{ wch: 12 }, { wch: 28 }, { wch: 16 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Movimientos");
      const fecha = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Movimientos_Bancarios_${fecha}.xlsx`);
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>{t("common.inicio")} /</span></Link>
        <span className={invoiceStyles.active}> {t("movimientos.titulo")}</span>
      </span>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: 6 }}>
        <div>
          <h2 className="title mt-2 mb-2">{t("movimientos.titulo")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("movimientos.subtitulo")}</p>
        </div>
        <div className="d-flex flex-wrap gap-2" style={{ marginTop: 8 }}>
          {isSuperUser && (
            <>
              <button onClick={() => setModal({ tipo: "crear" })} style={btnAdmin("#f97316")}
                title="Crear un movimiento a mano (por ejemplo, el de un recibo que no lo generó)">
                <i className="fa-solid fa-plus"/>Crear movimiento
              </button>
              <button onClick={() => setVerDuplicados(true)} style={btnAdmin("#f59e0b")}>
                <i className="fa-solid fa-clone"/>Posibles duplicados
              </button>
              <button onClick={() => setModal({ tipo: "bitacora" })} style={btnAdmin("#8b5cf6")}>
                <i className="fa-solid fa-clock-rotate-left"/>Bitácora
              </button>
            </>
          )}
          <button onClick={exportarExcel} disabled={exporting || count === 0}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 18px",
              background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.35)",
              borderRadius: 8, color: "#4ade80", fontSize: "0.85rem", fontWeight: 600,
              cursor: exporting || count === 0 ? "not-allowed" : "pointer",
              opacity: exporting || count === 0 ? 0.5 : 1,
            }}>
            <i className={`fa-solid ${exporting ? "fa-spinner fa-spin" : "fa-file-excel"}`}/>
            {exporting ? t("movimientos.exportando") : t("movimientos.exportarExcel")}
          </button>
        </div>
      </div>

      {aviso && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "9px 14px",
          borderRadius: 8, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.35)",
          color: "var(--text-primary)", fontSize: "0.84rem" }}>
          <i className="fa-solid fa-circle-check" style={{ color: "#22c55e" }}/>{aviso}
        </div>
      )}

      <div className={invoiceStyles["table-container"]}>
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder={t("movimientos.buscarPlaceholder")}
          loading={loading}
          activeFilterCount={activeFilterCount}
          onClearFilters={() => { setFechaDesde(""); setFechaHasta(""); setCuentaId(""); setPage(1); }}
          filters={
            <>
              <FilterField label={t("movimientos.filtroCuenta")}>
                <FilterSelect value={cuentaId} onChange={(e) => { setCuentaId(e.target.value); setPage(1); }}>
                  <option value="">{t("movimientos.todasLasCuentas")}</option>
                  {cuentas.map((c) => (
                    <option key={c.id} value={c.id}>{c.banco} - {c.numero_cuenta} ({c.moneda})</option>
                  ))}
                </FilterSelect>
              </FilterField>
              <FilterField label={t("common.fechaDesde")}>
                <FilterInput type="date" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.fechaHasta")}>
                <FilterInput type="date" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPage(1); }} />
              </FilterField>
            </>
          }
        />

        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("movimientos.colFecha")}</th>
                <th>{t("movimientos.colCuenta")}</th>
                <th>{t("movimientos.colDocumento")}</th>
                <th>{t("movimientos.colDescripcion")}</th>
                <th><div style={{ textAlign: "right" }}>{t("movimientos.colDebito")}</div></th>
                <th><div style={{ textAlign: "right" }}>{t("movimientos.colCredito")}</div></th>
                <th className="text-center">{t("movimientos.colEstado")}</th>
                {isSuperUser && <th className="text-center">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={COLS} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8, color: "#f97316" }}/>
                  {t("movimientos.cargando")}
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={COLS} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }}/>
                  {t("movimientos.sinMovimientos")}
                </td></tr>
              ) : items.map((m) => {
                const simbolo = cuentaSimbolo(m.cuenta_bancaria);
                return (
                  <tr key={m.id}>
                    <td>{fmtDate(m.fecha)}</td>
                    <td style={{ color: "var(--text-primary)", fontSize: 13 }}>{cuentaNombre(m.cuenta_bancaria)}</td>
                    <td>{m.documento || "-"}</td>
                    <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={m.descripcion}>
                      {m.vinculo && (
                        <span title={`Vinculado al ${m.vinculo.tipo === "CXP" ? "pago" : "recibo"} ${m.vinculo.numero}${m.vinculo.filial ? ` (filial ${m.vinculo.filial})` : ""}`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 6,
                            padding: "0 6px", borderRadius: 4, fontSize: "0.68rem", fontWeight: 700,
                            background: "rgba(59,130,246,0.12)", color: "#3b82f6" }}>
                          <i className="fa-solid fa-link" style={{ fontSize: "0.6rem" }}/>{m.vinculo.numero}
                        </span>
                      )}
                      {m.descripcion || "-"}
                    </td>
                    <td>
                      <div style={{ textAlign: "right", fontFamily: "monospace",
                        color: Number(m.debito) > 0 ? "#f87171" : "var(--text-muted)",
                        fontWeight: Number(m.debito) > 0 ? 600 : 400 }}>
                        {Number(m.debito) > 0 ? `${simbolo}${formatCurrency(m.debito)}` : "-"}
                      </div>
                    </td>
                    <td>
                      <div style={{ textAlign: "right", fontFamily: "monospace",
                        color: Number(m.credito) > 0 ? "#4ade80" : "var(--text-muted)",
                        fontWeight: Number(m.credito) > 0 ? 600 : 400 }}>
                        {Number(m.credito) > 0 ? `${simbolo}${formatCurrency(m.credito)}` : "-"}
                      </div>
                    </td>
                    <td className="text-center">
                      {m.conciliacion ? (
                        <span style={{ padding: "2px 9px", borderRadius: 5, fontSize: "0.72rem", fontWeight: 700,
                          background: "rgba(74,222,128,0.12)", color: "#4ade80" }}>
                          <i className="fa-solid fa-check" style={{ marginRight: 5 }}/>{t("movimientos.estadoConciliado")}
                        </span>
                      ) : (
                        <span style={{ padding: "2px 9px", borderRadius: 5, fontSize: "0.72rem", fontWeight: 700,
                          background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}>
                          {t("movimientos.estadoPendiente")}
                        </span>
                      )}
                    </td>
                    {isSuperUser && (
                      <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          <IconBtn icon="fa-pen" title="Editar movimiento"
                            onClick={() => setModal({ tipo: "editar", id: m.id })}/>
                          <IconBtn icon="fa-trash" title="Eliminar movimiento" color="#ef4444"
                            onClick={() => setModal({ tipo: "eliminar", id: m.id })}/>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }}/>
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("movimientos.registros")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
                {totales.map((tot) => {
                  const simbolo = tot.moneda === "USD" ? "$" : tot.moneda === "EUR" ? "€" : "₡";
                  return (
                    <span key={tot.moneda} style={{ marginLeft: 14, fontSize: "0.82rem" }}>
                      <span style={{ color: "var(--text-muted)" }}>
                        {t("movimientos.totalDebitos")}:{" "}
                      </span>
                      <span style={{ fontFamily: "monospace", color: "#f87171", fontWeight: 600 }}>
                        {simbolo}{formatCurrency(tot.debito)}
                      </span>
                      <span style={{ color: "var(--text-muted)", marginLeft: 10 }}>
                        {t("movimientos.totalCreditos")}:{" "}
                      </span>
                      <span style={{ fontFamily: "monospace", color: "#4ade80", fontWeight: 600 }}>
                        {simbolo}{formatCurrency(tot.credito)}
                      </span>
                    </span>
                  );
                })}
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {isSuperUser && verDuplicados && (
        <DuplicadosModal version={versionDup} onClose={() => setVerDuplicados(false)}
          onEditar={(id) => setModal({ tipo: "editar", id })}
          onEliminar={(id) => setModal({ tipo: "eliminar", id })}/>
      )}
      {isSuperUser && modal?.tipo === "crear" && (
        <CrearMovimientoModal cuentas={cuentas} onClose={() => setModal(null)} onDone={alTerminar}/>
      )}
      {isSuperUser && modal?.tipo === "editar" && (
        <EditarMovimientoModal movId={modal.id} cuentas={cuentas} onClose={() => setModal(null)} onDone={alTerminar}/>
      )}
      {isSuperUser && modal?.tipo === "eliminar" && (
        <EliminarMovimientoModal movId={modal.id} onClose={() => setModal(null)} onDone={alTerminar}/>
      )}
      {isSuperUser && modal?.tipo === "bitacora" && (
        <BitacoraModal cuentas={cuentas} onClose={() => setModal(null)}/>
      )}
    </>
  );
}

function btnAdmin(color) {
  return {
    display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px",
    background: `${color}1a`, border: `1px solid ${color}59`, borderRadius: 8,
    color, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
  };
}
